import { useState, useEffect, useCallback, useRef } from 'react'
import { shallowEqual, useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Auth, API } from 'aws-amplify'
import './WorldContainer.scss'
import '../../Animations.css'
import { useAppContext } from '../../Lib/contextLib'
import PopUp from '../../Popup/logoutPopup'

import MediaService from '../../services/MediaService'
import { getVideo } from '../../s3Func/s3Download'

import Learn from '../../components/World/Learn/Learn'
import Celebrate from '../../Celebrate/Celebrate'
import PhotoTime from '../../components/World/PhotoTime/PhotoTime'
import Surprise from '../../components/World/Surprise/Surprise'
import SingingTime from '../../components/World/SingingTime/SingingTime'
import WorldChooseReward from '../../components/World/WorldChooseRewards'
import WorldActions from '../../components/World/WorldActions'

import { CloudfrontVideoFormat } from '../../videos/CloudfrontVideoFormat'

import { getUserContent, getSlideImages } from '../../utils'

import GIFT_SOUND from '../assets/ta_da_gift.mp3'

/**
 * Module1World is a base template for all worlds that follow it. In this case, this is for the
 * beach scene, but this code has been copied and pasted directly to worlds 1-5 with only slight
 * name changes to apply to each world. After world 5, there is an additional component of
 * Follow The Beat, which will have a comment writeup in Module6World.
 * @returns The HTML code for Module1World
 */
const WorldContainer = ({
	name,
	module,
	moduleName,
	moduleConfig,
	slidesConfig,
	practice,
	route,
	worldImages,
	worldRewards,
	showRewardSelection,
	uuid,
	children,
	onPracticeClick,
	displayPracticeVideo,
	displayEndingVideo,
	onRewardKeyClick,
	onRewardSelected,
	keyIcon,
}) => {
	const dispatch = useDispatch()
	const { login } = useSelector((state) => state.loginReducer, shallowEqual)
	const { userHasAuthenticated } = useAppContext()

	const [displayLearnVideo, setDisplayLearnVideo] = useState(false)
	const [displayCelebrateVideo, setDisplayCelebrateVideo] = useState(false)
	const [displayPhotoTime, setDisplayPhotoTime] = useState(false)
	const [displaySingingTime, setDisplaySingingTime] = useState(false)
	const [displayIntroVideo, setDisplayIntroVideo] = useState(false)
	const [displaySurprise, setDisplaySurprise] = useState(false)

	const [surpriseConfig, setSurpriseConfig] = useState(false)

	const [dayProgress, setDayProgress] = useState(0)

	const [learnVideoConfig, setLearnVideoConfig] = useState({})
	const [celebrateConfig, setCelebrateConfig] = useState({})
	const [dayIsCompleted, setDayIsCompleted] = useState(
		module.currentday <= module.day_completed
	)
	// 'TireSwing' for module 2 is not included
	// 'Hat' for module 4 is not included
	const rewardCheck = ['BirdBlue', 'BirdRed', 'Ladder', 'Stairs', 'Swing', 'TelescopeBlue', 'TelescopeRed', 
						 'TreeHouseBlue', 'TreeHouseRed', 'Goggles', 'HardHat',
						'Bow', 'Bandanna', 'TireSwing', 'Hat', 'MonkeybarsBlue', 'MonkeybarsYellow', 'SlideRed', 'SlideYellow', 'Hippo',
						'Flamingos']

	const [isOpen, setIsOpen] = useState(false)

	const firstUpdate = useRef(true)

	const navigate = useNavigate()

	const GIFT_IMAGE = MediaService.getImage('/Images/Gift.png', login)
	const LOGOUT_BUTTON = MediaService.getImage(
		'/Module1Images/Logout.png',
		login
	)

	const bouncingSound = MediaService.getVideo(
		'/Module1Videos/button_bouncing.mp3',
		login
	)

	const bounceAudio = new Audio(bouncingSound);

	const progressImages = [
		worldImages.progressBar0,
		worldImages.progressBar1,
		worldImages.progressBar2,
		worldImages.progressBarCompleted,
	]

	const updateProgress = useCallback(async () => {
		const payload = {
			body: {
				id: login.studentid,
				module: module.name,
				day_completed: module.currentday,
				time_completed: new Date().toDateString(),
			},
		}
		await API.put('TIR', '/v3/student/progress', payload)
		.then((response) => {
		})
		.catch((error) => {
		});
	})

	const updateDB = useCallback(async () => {
		const payload = {
			body: {
				id: module.moduleID,
				day_completed: module.day_completed,
				step_completed: module.stepCompleted,
				game_details: JSON.stringify(module.game_details),
			},
		}

		try {
			await API.put('TIR', '/v3/student/module', payload)
		} catch (e) {
			if (
				module.stepCompleted === 'notstarted' ||
				module.stepCompleted === 'learn' ||
				module.stepCompleted === 'practice' ||
				module.stepCompleted === 'celebrate'
			) {
				window.alert('Connection is lost. Please login again.')
				dispatch({ type: 'CLEAR' })
				Auth.signOut()
				navigate('/')
			}
		}
		const myInit2 = {
			body: {
			  id: login.studentid,
			  current_module: name,
			},
		  };
		await API.put('TIR', '/v3/student', myInit2)
		.then((response) => {
		})
		.catch((error) => {
		});
		setDayIsCompleted(module.currentday <= module.day_completed)
	})

	const checkProgressInTheGame = useCallback(() => {
		if (module.stepCompleted === 'notstarted') setDayProgress(0)
		if (module.stepCompleted === 'learn') setDayProgress(1)
		if (module.stepCompleted === 'practice') setDayProgress(2)
		if (module.stepCompleted === 'celebrate') setDayProgress(3)
		if (module.stepCompleted === 'phototime') setDayProgress(1)
		// if (module.stepCompleted === "singingtime") setDayProgress(3);

		// setDayIsCompleted(module.currentday <= module.day_completed);

		if (dayIsCompleted || login.grade === "Teacher Mode" || login.grade === 'Free Play') {
			dispatch({ type: `${moduleName}_TOGGLE_REVIEW_ICON`, payload: true })
			dispatch({ type: `${moduleName}_SET_KEY`, payload: 3 })
		} else {
			dispatch({ type: `${moduleName}_TOGGLE_REVIEW_ICON`, payload: false })
		}
	})

	useEffect(() => {
		// Save progress on DB
		if (firstUpdate.current) {
			firstUpdate.current = false
		} else {
			updateDB()
		}
		// Check game progress
		checkProgressInTheGame()
	}, [module.stepCompleted])

	// Init component
	useEffect(() => {
		const { currentday, stepCompleted } = module
		const hasIntroVideo = currentday === 4 || currentday === 5

		if (hasIntroVideo && stepCompleted === 'notstarted' && !dayIsCompleted) {
			setDisplayIntroVideo(true)
		}
	}, [])
	useEffect(() => {
		if((module.reviewIcon === true || login.grade === "Teacher Mode") && module.sectionCompleted === 3 ) {
			updateProgress();
			module.sectionCompleted = 0;
		}
	}, [module.sectionCompleted])

	const handleLearnClick = useCallback(async () => {
		const learnConfig = { back: route, destiny: route }

		const { learn } = moduleConfig[`day${module.currentday}`]

		// Set introduction video
		const learnVideo = learn.video.includes('influencer-id')
			? await getUserContent(learn.video, module.StudentID, login.influencerid, login)
			: learn.video

		learnConfig.background = worldImages.worldBackground
		learnConfig.step_completed = `${moduleName}_SET_stepCompleted`
		learnConfig.video = learnVideo

		// Set slides
		learnConfig.slidesConfig = slidesConfig
		
		setLearnVideoConfig(learnConfig)
		setDisplayLearnVideo(true)
	})

	const handleCelebrateClick = useCallback(() => {
		const { celebrate } = moduleConfig[`day${module.currentday}`]
		const { video, afterVideo, text, name } = celebrate
		setCelebrateConfig({ video, afterVideo, text, name })
		setDisplayCelebrateVideo(true)
	})

	const handlePhotoTimeClick = useCallback(() => setDisplayPhotoTime(true))
	const handleSingingTimeClick = useCallback(() => setDisplaySingingTime(true))

	const handleRewardSelected = useCallback(async () => {updateProgress(); onRewardSelected();})

	const handleGiftClick = useCallback(async () => {
		const { surprise } = moduleConfig[`day${module.currentday}`]
		let { slides, song, video } = surprise

		slides = await getSlideImages(slides, module, login)
		song = await getVideo(song, login)

		setSurpriseConfig({ video, song, slides })
		setDisplaySurprise(true)
	})

	const handleSurpriseEnd = () => {
		dispatch({ type: `${moduleName}_SET_stepCompleted`, payload: 'surprise' })
		setDisplaySurprise(false)
	}

	const handleKeyClick = (event) => {
		let button
		if (event.nativeEvent.target.nodeName === 'IMG') {
			button = event.nativeEvent.target.offsetParent
		} else {
			button = event.nativeEvent.path.find((p) => p.nodeName === 'BUTTON')
		}
		button.id = 'fade-out'
		onRewardKeyClick()
	}

	const togglePopup = () => setIsOpen(!isOpen)

	const getGameProgressImage = () => {
		if (module.stepCompleted === 'singingtime') {
			return progressImages[3]
		}

		return progressImages[dayProgress]
	}

	function sleep(ms) {
		return new Promise((resolve) => setTimeout(resolve, ms));
	  }

	// The actual HTML code of the module
	return (
		<>
			<div style={{ position: 'fixed', zIndex: 6 }}>
				{isOpen && (
					<PopUp
						content={
							<>
								<b id="title">
									Are you sure you want to leave?
									<br />
								</b>
								<button
									id="logoutButton1"
									className="logoutButton"
									onClick={() => {
										navigate('/')
										userHasAuthenticated(false)
										localStorage.setItem('token', 'false')
									}}
								>
									YES
								</button>
								<button
									id="logoutButton2"
									className="logoutButton"
									onClick={togglePopup}
								>
									NO
								</button>
							</>
						}
						handleClose={togglePopup}
					/>
				)}
			</div>

			<WorldActions
				showLearn={
					module.stepCompleted === 'notstarted' &&
					!module.reviewIcon &&
					module.currentday !== 4
				}
				showPractice={module.stepCompleted === 'learn'}
				showCelebrate={module.stepCompleted === 'practice'}
				showReview={module.reviewIcon}
				showReward={(login.grade === "Teacher Mode" || login.grade === 'Free Play') ? true : false}
				showPhotoTime={
					module.currentday === 4 &&
					module.stepCompleted === 'notstarted' &&
					!module.reviewIcon
				}
				showSingingTime={
					module.currentday === 4 && module.stepCompleted === 'phototime'
				}
				showClosingVideo={
					module.currentday === 9 && module.stepCompleted === 'surprise'
				}
				onLearnClick={handleLearnClick}
				onPracticeClick={onPracticeClick}
				onCelebrateClick={handleCelebrateClick}
				onPhotoTimeClick={handlePhotoTimeClick}
				onSingingTimeClick={handleSingingTimeClick}
				onRewardClick={onRewardKeyClick}
				moduleDay={module.currentday}
			/>

			{displayLearnVideo && (
				<Learn
					video={learnVideoConfig.video}
					destiny={learnVideoConfig.destiny}
					slidesConfig={learnVideoConfig.slidesConfig}
					song={learnVideoConfig.song}
					onClose={() => {setDisplayLearnVideo(false); module.sectionCompleted = module.sectionCompleted + 1}}
					background={learnVideoConfig.background}
					setDisplayLearn={setDisplayLearnVideo}
					lyrics={learnVideoConfig.lyrics}
					step_completed={learnVideoConfig.step_completed}
					module={module}
					moduleName={moduleName}
					login={login}
					openPhotoTime={handlePhotoTimeClick}
				/>
			)}

			{displayPracticeVideo && practice}

			{displayCelebrateVideo && (
				<Celebrate
					module={module}
					celebrateVideo={celebrateConfig.video}
					afterCelebrateVideo={celebrateConfig.afterVideo}
					text={celebrateConfig.text}
					name={celebrateConfig.name}
					animal={celebrateConfig.animal}
					background={worldImages.worldBackground}
					setDisplayCelebrate={setDisplayCelebrateVideo}
					moduleNum={moduleName}
				/>
			)}

			{(!module.reviewIcon && ((module.stepCompleted === 'celebrate' && module.currentday !== 9) ||
				module.stepCompleted === 'singingtime' ||
				module.stepCompleted === 'surprise')) && (
				<div>
					<audio autoPlay>
						<source src={bouncingSound}></source>
					</audio>

					<button
						className = {uuid === "expressions" ? "key-reward-small" : "key-reward"}
						id="bounceIcons"
						onClick={(event) => handleKeyClick(event)}
					>
						<img src={worldImages.keyReward} alt="reward key" />
					</button>
				</div>
			)}

			{module.stepCompleted === 'celebrate' && module.currentday === 9 && (
				<>
					<audio autoPlay>
						<source src={GIFT_SOUND}></source>
					</audio>
					<button
						className="gift-reward"
						id="bounceIcons"
						onClick={handleGiftClick}
					>
						<img src={GIFT_IMAGE} alt="reward gift" />
					</button>
				</>
			)}

			{showRewardSelection && (
				<WorldChooseReward
					module={module}
					moduleName={moduleName}
					rewardOption1={
						worldRewards[moduleConfig[`day${module.currentday}`].rewardOption1]
					}
					rewardOption2={
						worldRewards[moduleConfig[`day${module.currentday}`].rewardOption2]
					}
					onRewardSelectionEnd={handleRewardSelected}
				/>
			)}

			{displaySurprise && (
				<Surprise
					background={worldImages.worldBackground}
					setDisplaySurprise={setDisplaySurprise}
					back={route}
					video={surpriseConfig.video}
					song={surpriseConfig.song}
					slides={surpriseConfig.slides}
					onSurpriseEnded={handleSurpriseEnd}
				/>
			)}

			{displayEndingVideo && (
				<CloudfrontVideoFormat
					source={moduleConfig.day9.conclusionVideo}
					ended={ async () => {
						dispatch({
							type: `${moduleName}_SET_stepCompleted`,
							payload: 'notstarted',
						})
						await sleep(100)
						navigate(`${route}/navigation`)}}
				/>
			)}

			{module.currentday === 4 && displayIntroVideo && (
				<CloudfrontVideoFormat
					source={moduleConfig.day4.introVideo}
					ended={() => {setDisplayIntroVideo(false); sleep(500).then(() => {
						bounceAudio.play()
					  });}}
				/>
			)}

			{module.currentday === 5 && displayIntroVideo && (
				<CloudfrontVideoFormat
					source={moduleConfig.day5.introVideo}
					ended={() =>{setDisplayIntroVideo(false); sleep(500).then(() => {
						bounceAudio.play()
					  });}}
				/>
			)}

			{module.currentday >= 4  &&  (
				<>
					{displayPhotoTime && (
						<PhotoTime
							introVideo={moduleConfig.day4.photoTime.video1}
							poseVideo={moduleConfig.day4.photoTime.video3}
							background={worldImages.worldBackground}
							fourSillyPhotos={moduleConfig.day4.photoTime.video2}
							name={uuid}
							setDisplayPhotoTime={setDisplayPhotoTime}
							module={module}
							moduleNum={moduleName}
						/>
					)}

					{displaySingingTime && (
						<SingingTime
							introVideo={moduleConfig.day4.singingTime.intro}
							singAlongMusic={moduleConfig.day4.singingTime.singAlongMusic}
							instrumentalMusic={
								moduleConfig.day4.singingTime.instrumentalMusic
							}
							singingAlongVideo={
								moduleConfig.day4.singingTime.singingAlongVideo
							}
							instrumentalVideo={
								moduleConfig.day4.singingTime.instrumentalVideo
							}
							background={worldImages.worldBackground}
							setDisplaySingingTime={setDisplaySingingTime}
							module={module}
							moduleNum={moduleName}
							name={uuid}
						/>
					)}
				</>
			)}

			{
				<div
					className="bg-image"
					style={{
						backgroundImage: `url(${worldImages.worldBackground})`,
					}}
				>
					<nav>
						<div className="actions-left">
							<button>
								<img
									src={LOGOUT_BUTTON}
									alt="Logout Button"
									onClick={togglePopup}
								/>
							</button>

							<button onClick={() => navigate(`${route}/navigation`)}>
								<img
									src={worldImages.journeyImage}
									alt="Navigation blue world"
								/>
							</button>
						</div>

						{module.reviewIcon ? 
						<div className="actions-right">
							{moduleName === 'MODULE1' ? (
								<div className="progressBar2">
									<img
										src={progressImages[3]}
										className={keyIcon}
										alt="progress key"
									/>
									<h3
										style={{
											marginLeft: '-30%',
											zIndex: 10,
										}}
									>
										{module.currentday === 4
											? 2
											: 3}
										/{module.currentday === 4 ? 2 : 3}
									</h3>
								</div>
							) : (
								<div className="progressBar2">
									<img
										className="progressKey2"
										src={progressImages[3]}
										alt="key Icon"
									/>
									<h3 className="progresstext2">
										{(module.currentday === 4)
											? 2
											: 3}
										/{module.currentday === 4 ? 2 : 3}
									</h3>
								</div>
							)}
						</div>
						:
						<div className="actions-right">
							{moduleName === 'MODULE1' ? (
								<div className="progressBar2">
									<img
										src={getGameProgressImage()}
										className={keyIcon}
										alt="progress key"
									/>
									<h3
										style={{
											marginLeft: '-30%',
											zIndex: 10,
										}}
									>
										{module.currentday === 4 &&
										module.stepCompleted === 'singingtime'
											? 2
											: dayProgress}
										/{module.currentday === 4 ? 2 : 3}
									</h3>
								</div>
							) : (
								<div className="progressBar2">
									<img
										className="progressKey2"
										src={getGameProgressImage()}
										alt="key Icon"
									/>
									<h3 className="progresstext2">
										{(module.currentday === 4) &
										(module.stepCompleted === 'singingtime')
											? 2
											: dayProgress}
										/{module.currentday === 4 ? 2 : 3}
									</h3>
								</div>
							)}
						</div>}
					</nav>

					<main>{children}</main>


					<div className="world-rewards">
						{Object.keys(worldRewards).map((key) => {
							if(!rewardCheck.includes(key)){
								return (
									module.rewards[key] && (
										<img
											key={`${key.toLowerCase()}_image`}
											className={`world-rewards ${key} ${moduleName}`}
											src={worldRewards[key]}
											alt={`${key.toLowerCase()}`}
										/>
									)
								)
							}
							else if((key === 'TireSwing' && moduleName === 'MODULE2') || (key === 'Hat' && moduleName === 'MODULE4')){
								return (
									module.rewards[key] && (
										<img
											key={`${key.toLowerCase()}_image`}
											className={`world-rewards ${key} ${moduleName}`}
											src={worldRewards[key]}
											alt={`${key.toLowerCase()}`}
										/>
									)
								)
							}
						})}
					</div>
				</div>
			}
		</>
	)
}

export default WorldContainer
